import React, { useEffect, useState } from 'react';
import { useAPI } from '../../contexts/APIContext';
import { showToast } from '../Toast';
import VoiceRecorder from './VoiceRecorder';
import {
  FREDOKA,
  VX,
  PipStudioHeader,
  RecordVoiceButton,
  VoiceBadgeGrid,
  VoiceStyles,
  orderVoices,
  usePreviewPlayer,
  useVoiceOptions,
  useVoiceShortlist,
} from './VoicePicker';

// The character page's Voice window (the iOS change sheet on the web): the
// current voice, "Record your voice", and 8 voices led by the Best match for
// this character's picture. Changing to a catalog voice costs stars; the first
// recording is free.

const serverReason = (r, fallback) => r?.detail || r?.error || r?.message || fallback;

export default function CharacterVoiceModal({ isOpen, onClose, character, onUpdated }) {
  const { apiClient } = useAPI();
  const { voices, cost, loading } = useVoiceOptions();
  const shortlist = useVoiceShortlist(isOpen ? character?.animated_image_url : null);
  const { playing, toggle, stop } = usePreviewPlayer();
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setChoice(null);
      setRecorderOpen(false);
      setProblem('');
      stop();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !character) return null;
  const currentId = character.voice_id;
  const isCloned = character.voice_source === 'cloned';
  const current = voices.find((v) => v.voice_id === currentId);
  const grid = orderVoices(voices, shortlist).filter((v) => v.voice_id !== currentId).slice(0, 8);
  const recordPrice = isCloned ? `${cost}★` : 'free';

  const applyChange = async () => {
    if (!choice || busy) return;
    setBusy(true);
    setProblem('');
    try {
      const r = await apiClient.setCharacterVoice(character.id, choice.voice_id);
      if (r?.status !== 'success') throw new Error(serverReason(r, "Couldn't change the voice. Please try again."));
      showToast(`${character.name} now sounds like ${choice.name}!`, 'success');
      onUpdated?.({ voice_id: r.voice_id || choice.voice_id, voice_source: 'preset', pixley_stars: r.pixley_stars });
      onClose();
    } catch (e) {
      setProblem(e?.message || "Couldn't change the voice. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const applyRecording = async (file) => {
    setRecorderOpen(false);
    setBusy(true);
    setProblem('');
    try {
      const r = await apiClient.cloneCharacterVoice(character.id, [file]);
      if (r?.status !== 'success') throw new Error(serverReason(r, "Couldn't save your recording. Please try again."));
      showToast(`${character.name} now speaks with your voice!`, 'success');
      onUpdated?.({ voice_id: r.voice_id, voice_source: 'cloned', pixley_stars: r.pixley_stars });
      onClose();
    } catch (e) {
      // e.g. "That recording is very quiet — hold the phone closer…"
      setProblem(e?.message || "Couldn't save your recording. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${character.name}'s voice`}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(11,26,51,0.45)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <VoiceStyles />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px, 100%)',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 24,
          border: `2.5px solid ${VX.ink}`,
          boxShadow: `0 6px 0 ${VX.ink}, 0 30px 80px rgba(11,26,51,0.35)`,
          background: '#fff',
          fontFamily: FREDOKA,
          color: VX.ink,
        }}
      >
        <div style={{ position: 'relative' }}>
          <PipStudioHeader line1="Change their" line2="voice!" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ position: 'absolute', top: 10, left: 10, width: 34, height: 34, borderRadius: '50%', border: `2px solid ${VX.ink}`, background: '#fff', color: VX.ink, fontWeight: 700, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Current voice */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 16, background: '#F0F7FF', border: '1.5px solid rgba(11,26,51,0.08)' }}>
            <span style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'rgba(175,227,254,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              {isCloned ? '🎙' : current?.mascot_url ? <img src={current.mascot_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '〰'}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: VX.inkSoft }}>VOICE NOW</span>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700 }}>{isCloned ? 'Your recording' : current?.name || 'A Pixley voice'}</span>
            </span>
            {!isCloned && current?.preview_url && (
              <button
                type="button"
                onClick={() => toggle(current)}
                aria-label={playing === current.voice_id ? 'Stop' : `Hear ${current.name}`}
                style={{ width: 38, height: 38, borderRadius: '50%', border: `1.8px solid ${VX.ink}`, background: VX.blue, color: '#fff', cursor: 'pointer' }}
              >
                {playing === current.voice_id ? '■' : '▶'}
              </button>
            )}
          </div>

          <RecordVoiceButton
            onClick={() => { stop(); setRecorderOpen(true); }}
            label={`Record your voice · ${recordPrice}`}
          />

          {loading || !shortlist.ready ? (
            <div style={{ textAlign: 'center', color: VX.inkSoft, fontSize: 14, padding: '36px 0' }}>Finding voices that fit…</div>
          ) : (
            <VoiceBadgeGrid
              voices={grid}
              suggestedId={shortlist.suggested?.voice_id}
              selectedId={choice?.voice_id}
              playing={playing}
              onPick={(v) => { toggle(v); setChoice(v); setProblem(''); }}
            />
          )}

          {problem && (
            <div role="alert" style={{ color: '#D12F4A', fontSize: 14, fontWeight: 500, textAlign: 'center' }}>{problem}</div>
          )}

          {choice && (
            <button
              type="button"
              className="vx-press"
              disabled={busy}
              onClick={applyChange}
              style={{
                padding: '13px 16px',
                borderRadius: 16,
                border: `2px solid ${VX.ink}`,
                background: VX.gold,
                boxShadow: '0 4px 0 #E89410',
                color: VX.ink,
                fontFamily: FREDOKA,
                fontSize: 16,
                fontWeight: 700,
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Changing…' : `Change to ${choice.name} · ${cost}★`}
            </button>
          )}
          {busy && !choice && <div style={{ textAlign: 'center', color: VX.inkSoft, fontSize: 14 }}>Making the voice from your recording…</div>}
        </div>
      </div>

      {/* Clicks inside the recorder must not reach the backdrop above (it closes the window). */}
      <div onClick={(e) => e.stopPropagation()}>
      <VoiceRecorder isOpen={recorderOpen} onClose={() => setRecorderOpen(false)} onDone={applyRecording} />
      </div>
    </div>
  );
}
